import { useEffect, useState, type ReactNode } from 'react'
import { getMaintenanceEvent } from '~/api/maintenance'
import type { AdminMaintenanceEventDetail } from '~/api/types'
import { SidePanelDetail } from '~/components/ui/side-panel-detail'
import { formatDateTime } from '~/lib/utils'

// 유지하기 변경 이벤트 상세 슬라이드오버. 변경 내용 전문 + 영향(impact).
// impact가 { affectedCount, guide } 형태면 구조화 표시, 아니면 JSON pretty 표시.

function isImpactObject(
    value: unknown
): value is { affectedCount?: unknown; guide?: unknown } {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function MaintenanceDetailPanel({
    eventId,
    onClose,
}: {
    eventId: string
    onClose: () => void
}) {
    const [detail, setDetail] = useState<AdminMaintenanceEventDetail | null>(
        null
    )
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        getMaintenanceEvent(eventId)
            .then((data) => {
                if (alive) setDetail(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [eventId])

    return (
        <SidePanelDetail
            title='변경 이벤트 상세'
            data={detail}
            loading={loading}
            error={error}
            onClose={onClose}>
            {(detail) => {
                const impact = detail.impact
                const structured =
                    isImpactObject(impact) &&
                    (impact.affectedCount !== undefined ||
                        impact.guide !== undefined)

                return (
                    <>
                        <section>
                            <h3 className='text-foreground mb-2 text-sm font-bold'>
                                기본정보
                            </h3>
                            <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
                                <Field
                                    label='평가 ID'
                                    value={detail.assessmentId ?? '-'}
                                />
                                <Field
                                    label='생성일'
                                    value={formatDateTime(detail.createdAt)}
                                />
                            </dl>
                        </section>

                        <section>
                            <h3 className='text-foreground mb-2 text-sm font-bold'>
                                변경 내용
                            </h3>
                            <p className='text-foreground whitespace-pre-wrap'>
                                {detail.changeDescription ?? '-'}
                            </p>
                        </section>

                        <section>
                            <h3 className='text-foreground mb-2 text-sm font-bold'>
                                영향
                            </h3>
                            {impact == null ? (
                                <p className='text-muted-foreground text-sm'>
                                    영향 정보가 없어요.
                                </p>
                            ) : structured ? (
                                <dl className='grid grid-cols-2 gap-x-4 gap-y-2 text-sm'>
                                    {(impact as { affectedCount?: unknown })
                                        .affectedCount !== undefined ? (
                                        <Field
                                            label='영향 항목 수'
                                            value={String(
                                                (
                                                    impact as {
                                                        affectedCount?: unknown
                                                    }
                                                ).affectedCount
                                            )}
                                        />
                                    ) : null}
                                    {(impact as { guide?: unknown }).guide !==
                                    undefined ? (
                                        <div className='col-span-2'>
                                            <dt className='krds-label'>
                                                가이드
                                            </dt>
                                            <dd className='text-foreground mt-0.5 whitespace-pre-wrap'>
                                                {String(
                                                    (impact as { guide?: unknown })
                                                        .guide
                                                )}
                                            </dd>
                                        </div>
                                    ) : null}
                                </dl>
                            ) : (
                                <pre className='bg-secondary border-border text-foreground overflow-x-auto rounded-[var(--radius)] border p-3 text-xs'>
                                    {JSON.stringify(impact, null, 2)}
                                </pre>
                            )}
                        </section>
                    </>
                )
            }}
        </SidePanelDetail>
    )
}

function Field({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div>
            <dt className='krds-label'>{label}</dt>
            <dd className='text-foreground mt-0.5'>{value}</dd>
        </div>
    )
}
